import * as React from 'react';
import { Editor } from 'react-draft-wysiwyg';
import '../../../node_modules/react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { createApiCaller } from '../../infrastructure/api/ApiFactory';
import { getBackendBaseUrl } from '../../infrastructure/BackendUrlResolver';
import { isNullOrUndefined } from '../../infrastructure/ValidationHelper';
import { EditorState } from 'draft-js';

export interface RichTextEditorProps {
    onEditorChange?: (editorState: EditorState) => void;
    placeholder?: string;
    readOnly?: boolean;
    editorState: EditorState;
}

class RichTextEditor extends React.Component<RichTextEditorProps, any> {
    constructor(props: RichTextEditorProps) {
        super(props);

        this.uploadCallback = this.uploadCallback.bind(this);
        this.onContentChange = this.onContentChange.bind(this);
    }

    onContentChange(e: any) {
        if (this.props.onEditorChange !== undefined) {
            this.props.onEditorChange(e);
        }
    }

    uploadCallback(file: File) {
        var apiCaller = createApiCaller();
        var formData = new FormData();
        formData.append('file', file);

        return new Promise((resolve, reject) => {
            let uploadPromise = apiCaller.uploadImage(formData);
            uploadPromise.then(response => {
                let imageLink = getBackendBaseUrl() + '/api/image/' + response;
                resolve({ data: { link: imageLink } });
            });
        });
    }

    getReadOnly(): boolean {
        return this.props.readOnly !== undefined ? this.props.readOnly : false;
    }

    getPlaceholder(): string {
        return this.props.placeholder !== undefined ? this.props.placeholder : '';
    }

    // if (embeddedLink.indexOf("youtube") >= 0) {
    //     embeddedLink = embeddedLink.replace("watch?v=", "embed/");
    //     embeddedLink = embeddedLink.replace("/watch/", "/embed/");
    //     embeddedLink = embeddedLink.replace("youtu.be/", "youtube.com/embed/");
    // }

    render() {
        let editorState = EditorState.createEmpty();
        if (!isNullOrUndefined(this.props.editorState)) {
            editorState = this.props.editorState;
        }

        let readOnly = this.getReadOnly();
        let classes = 'rich-text-wrapper';
        if (readOnly) {
            classes += ' read-only';
        }

        return (
            <Editor
                wrapperClassName={classes}
                editorClassName='editor'
                toolbarClassName='toolbar'
                toolbarHidden={readOnly}
                readOnly={readOnly}
                editorState={editorState}
                placeholder={this.getPlaceholder()}
                onEditorStateChange={this.onContentChange}
                toolbar={
                    {
                        // Removed 'fontSize', 'fontFamily' and 'colorPicker' from options (and removed the associated objects)
                        options: ['inline', 'blockType', 'list', 'textAlign', 'link', 'embedded', 'emoji', 'image', 'remove', 'history'],
                        inline: {
                            inDropdown: false,
                            className: undefined,
                            component: undefined,
                            dropdownClassName: undefined,
                            // Removed 'monospace' from options (and removed the associated object)
                            options: ['bold', 'italic', 'underline', 'strikethrough', 'superscript', 'subscript'],
                            bold: { className: undefined },
                            italic: { className: undefined },
                            underline: { className: undefined },
                            strikethrough: { className: undefined },
                            monospace: { className: undefined },
                            superscript: { className: undefined },
                            subscript: { className: undefined },
                        },
                        blockType: {
                            inDropdown: true,
                            options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'Blockquote'],
                            className: undefined,
                            component: undefined,
                            dropdownClassName: undefined,
                        },
                        list: {
                            inDropdown: false,
                            className: undefined,
                            component: undefined,
                            dropdownClassName: undefined,
                            options: ['unordered', 'ordered', 'indent', 'outdent'],
                            unordered: { className: undefined },
                            ordered: { className: undefined },
                            indent: { className: undefined },
                            outdent: { className: undefined },
                        },
                        textAlign: {
                            inDropdown: false,
                            className: undefined,
                            component: undefined,
                            dropdownClassName: undefined,
                            options: ['left', 'center', 'right', 'justify'],
                            left: { className: undefined },
                            center: { className: undefined },
                            right: { className: undefined },
                            justify: { className: undefined },
                        },
                        link: {
                            inDropdown: false,
                            className: undefined,
                            component: undefined,
                            popupClassName: undefined,
                            dropdownClassName: undefined,
                            showOpenOptionOnHover: true,
                            defaultTargetOption: '_self',
                            options: ['link', 'unlink'],
                            link: { className: undefined },
                            unlink: { className: undefined },
                        },
                        embedded: {
                            className: undefined,
                            component: undefined,
                            popupClassName: undefined,
                            defaultSize: {
                                height: 'auto',
                                width: 'auto',
                            },
                        },
                        emoji: {
                            className: undefined,
                            component: undefined,
                            popupClassName: undefined,
                            emojis: [
                                '😀', '😁', '😂', '😃', '😉', '😋', '😎', '😍', '😗', '🤗', '🤔', '😣', '😫', '😴', '😌', '🤓',
                                '😛', '😜', '😠', '😇', '😷', '😈', '👻', '😺', '😸', '😹', '😻', '😼', '😽', '🙀', '🙈',
                                '🙉', '🙊', '👼', '👮', '🕵', '💂', '👳', '🎅', '👸', '👰', '👲', '🙍', '🙇', '🚶', '🏃', '💃',
                                '⛷', '🏂', '🏌', '🏄', '🚣', '🏊', '⛹', '🏋', '🚴', '👫', '💪', '👈', '👉', '👉', '👆', '🖕',
                                '👇', '🖖', '🤘', '🖐', '👌', '👍', '👎', '✊', '👊', '👏', '🙌', '🙏', '🐵', '🐶', '🐇', '🐥',
                                '🐸', '🐌', '🐛', '🐜', '🐝', '🍉', '🍄', '🍔', '🍤', '🍨', '🍪', '🎂', '🍰', '🍾', '🍷', '🍸',
                                '🍺', '🌍', '🚑', '⏰', '🌙', '🌝', '🌞', '⭐', '🌟', '🌠', '🌨', '🌩', '⛄', '🔥', '🎄', '🎈',
                                '🎉', '🎊', '🎁', '🎗', '🏀', '🏈', '🎲', '🔇', '🔈', '📣', '🔔', '🎵', '🎷', '💰', '🖊', '📅',
                                '✅', '❎', '💯',
                            ],
                        },
                        image: {
                            className: undefined,
                            component: undefined,
                            popupClassName: undefined,
                            urlEnabled: true,
                            uploadEnabled: true,
                            alignmentEnabled: true,
                            uploadCallback: this.uploadCallback,
                            inputAccept: 'image/gif,image/jpeg,image/jpg,image/png,image/svg',
                            alt: { present: false, mandatory: false },
                            defaultSize: {
                                height: 'auto',
                                width: 'auto',
                            }
                        },
                        remove: { className: undefined, component: undefined },
                        history: {
                            inDropdown: false,
                            className: undefined,
                            component: undefined,
                            dropdownClassName: undefined,
                            options: ['undo', 'redo'],
                            undo: { className: undefined },
                            redo: { className: undefined },
                        },
                    }
                }
            />
        );
    }
}

export default RichTextEditor;


// WEBPACK FOOTER //
// ./~/tslint-loader!./src/uiComponents/input/RichTextEditor.tsx